import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  useTheme,
  styled,
  CircularProgress,
  IconButton,
  Tooltip,
  Collapse
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchRedirections,
  createRedirection,
  updateRedirection,
  deleteRedirection,
} from '../../../Reducer/redirectionSlice';
import RedirectionTable from './RedirectionTable';
import RedirectionFilters from './RedirectionFilters';
import RedirectionModal from './RedirectionModal';
import { toast } from 'react-toastify';
import { Add, Refresh, FilterList } from '@mui/icons-material';

const StyledPageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
}));

const StyledPageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const StyledContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
}));

const RedirectionPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { data, totalItems, currentPage, loading } = useSelector((state) => state.redirection);

  const [filters, setFilters] = useState({
    match_type: '',
    redirect_type: '',
    status: '',
    category: '',
    search: '',
    sort: 'createdAt',
    order: 'DESC',
  });

  const [page, setPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false); // Toggle for filter visibility

  useEffect(() => {
    dispatch(fetchRedirections({ page: page + 1, filters }));
  }, [dispatch, page, filters]);

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPage(0);
  };

  const handleRefresh = () => {
    dispatch(fetchRedirections({ page: page + 1, filters }));
    toast.info('Redirections refreshed');
  };

  const handleAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setModalOpen(true);
  };

  const handleSubmit = (formData) => {
    const promise = editing
      ? dispatch(updateRedirection({ id: editing.id, payload: formData }))
      : dispatch(createRedirection(formData));

    promise
      .then(() => {
        toast.success(`Redirection ${editing ? 'updated' : 'created'} successfully`);
        dispatch(fetchRedirections({ page: page + 1, filters }));
      })
      .catch((error) => {
        toast.error(`Failed to ${editing ? 'update' : 'create'} redirection: ${error.message}`);
      });

    setModalOpen(false);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this redirection?")) {
      dispatch(deleteRedirection(id))
        .then(() => {
          toast.success("Redirection deleted successfully");
          dispatch(fetchRedirections({ page: page + 1, filters }));
        })
        .catch((error) => {
          toast.error("Failed to delete redirection: " + error.message);
        });
    }
  };

  return (
    <StyledContentBox>
      <StyledPageHeader>
        <StyledPageTitle variant="h5">
          URL Redirection Manager
          <Tooltip title="Refresh data">
            <IconButton onClick={handleRefresh} color="primary" size="small">
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </StyledPageTitle>

        <Box display="flex" alignItems="center" gap={2}>
          <Tooltip title="Toggle Filters">
            <IconButton
              onClick={() => setFilterOpen((prev) => !prev)}
              color={filterOpen ? 'primary' : 'default'}
            >
              <FilterList />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            color="primary"
            onClick={handleAdd}
            startIcon={<Add />}
            sx={{
              borderRadius: theme.shape.borderRadius,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
            }}
          >
            New Redirection
          </Button>
        </Box>
      </StyledPageHeader>

      <Collapse in={filterOpen}>
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2,
            borderRadius: theme.shape.borderRadius,
            transition: 'all 0.3s ease',
          }}
        >
          <RedirectionFilters filters={filters} onFilterChange={handleFilterChange} />
        </Paper>
      </Collapse>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{ borderRadius: theme.shape.borderRadius, overflow: 'hidden' }}
        >
          <RedirectionTable
            data={data}
            loading={loading}
            page={page}
            totalItems={totalItems}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </Paper>
      )}

      <RedirectionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        initialData={editing}
      />
    </StyledContentBox>
  );
};

export default RedirectionPage;
